// Sonda de version remota. Tres fuentes encadenadas, de mas a menos precisa.
//
// La idea es que ninguna falla individual deje al cliente ciego:
//
//   A. GET /version.json  — la fuente canonica. Trae severidad y metadata.
//   B. GET /index.html    — si version.json no esta (deploy viejo, 404, un
//                           proxy que lo come), el meta del HTML tiene el
//                           mismo buildId. Sin severidad, pero alcanza para
//                           detectar.
//   C. Deriva de assets   — si ni siquiera hay metas (alguien sirvio un HTML
//                           sin sellar), comparamos los nombres de los assets
//                           referenciados. Vite los emite con hash de
//                           contenido: si cambiaron, el bundle cambio. Es la
//                           red de seguridad que no depende de nada nuestro.
//
// Todas las requests van con no-store + query buster + Cache-Control de
// pedido. Redundante a proposito: distintos intermediarios respetan distintas
// señales, y esta request es justamente la que NO puede volver cacheada.

import { fingerprintFrom, metaFrom, shortHash } from './parse';
import { currentAssetFingerprint } from './stamp';
import type { ProbeResult, RemoteStamp, UpdateSeverity } from './types';

const TIMEOUT_MS = 8000;

function noStoreInit(signal: AbortSignal): RequestInit {
  return {
    signal,
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
    headers: {
      'Cache-Control': 'no-cache, no-store, max-age=0',
      Pragma: 'no-cache',
    },
  };
}

function bust(path: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}__vg=${Date.now().toString(36)}`;
}

async function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await run(ctrl.signal);
  } finally {
    clearTimeout(timer);
  }
}

function parseSeverity(raw: unknown): UpdateSeverity {
  return raw === 'forced' ? 'forced' : 'recommended';
}

function str(raw: unknown): string | null {
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/** A — version.json. */
async function probeVersionJson(base: string): Promise<RemoteStamp | null> {
  const res = await withTimeout((signal) => fetch(bust(`${base}version.json`), noStoreInit(signal)));
  if (!res.ok) return null;

  // Guarda contra el fallback SPA: CloudFront devuelve index.html con 200
  // para cualquier path inexistente. Si pedimos JSON y vuelve HTML, es eso.
  const ctype = res.headers.get('content-type') ?? '';
  if (!ctype.includes('json')) return null;

  const body: unknown = await res.json();
  if (typeof body !== 'object' || body === null) return null;
  const raw = body as Record<string, unknown>;
  const buildId = str(raw['buildId']);
  if (!buildId) return null;

  return {
    buildId,
    commitShort: str(raw['commitShort']),
    commitDate: str(raw['commitDate']),
    commitSubject: str(raw['commitSubject']),
    builtAt: str(raw['builtAt']),
    severity: parseSeverity(raw['severity']),
    minBuildId: str(raw['minBuildId']),
    source: 'version.json',
  };
}

/** B y C — index.html: meta sellado, y si no hay, deriva de los assets. */
async function probeIndexHtml(base: string): Promise<RemoteStamp | null> {
  const res = await withTimeout((signal) => fetch(bust(`${base}index.html`), noStoreInit(signal)));
  if (!res.ok) return null;
  const html = await res.text();

  const buildId = metaFrom(html, 'build');
  if (buildId) {
    return {
      buildId,
      commitShort: metaFrom(html, 'commit'),
      commitDate: metaFrom(html, 'commit-date'),
      commitSubject: null,
      builtAt: metaFrom(html, 'built-at'),
      severity: parseSeverity(metaFrom(html, 'severity')),
      minBuildId: null,
      source: 'index.html',
    };
  }

  // C — sin metas. Solo podemos decir "esto es igual o distinto a lo mio".
  const remoteFp = fingerprintFrom(html);
  if (!remoteFp) return null;
  const localFp = currentAssetFingerprint();
  return {
    // Sintetizamos un id estable por huella: si la huella no cambia, el
    // buildId tampoco, asi no gritamos update en cada chequeo.
    buildId: remoteFp === localFp ? 'asset-drift:same' : `asset-drift:${shortHash(remoteFp)}`,
    commitShort: null,
    commitDate: null,
    commitSubject: null,
    builtAt: null,
    severity: 'recommended',
    minBuildId: null,
    source: 'asset-drift',
  };
}

/**
 * Consulta la version publicada. `base` es el path del portal (normalmente
 * '/'), con barra final.
 */
export async function probeRemote(base = '/'): Promise<ProbeResult> {
  const errors: string[] = [];

  for (const [label, fn] of [
    ['version.json', probeVersionJson],
    ['index.html', probeIndexHtml],
  ] as const) {
    try {
      const stamp = await fn(base);
      if (stamp) {
        // El caso C con huella identica no es una version: es "no hay nada
        // nuevo". Lo devolvemos como el buildId local para que el comparador
        // no lo tome como diferencia.
        return { ok: true, stamp };
      }
      errors.push(`${label}: sin sello utilizable`);
    } catch (err) {
      errors.push(`${label}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  return { ok: false, error: errors.join(' | ') };
}
